import React from "react";

import FormSection from "../../FundForm/FormSection";
import useMifosProduct from "../../../../../custom-hooks/useMifosProduct";
import { useGetTermsAndConditions } from "../../../../DigitalBank/InvestmentTermsAndConditions/Hooks/useTermsAndCondtions";
import { getCurrencyInfo } from "../../../helper";
import {
  formatAmount,
  formatDate,
  humanise,
  isBlank,
  TSavedFundDetail,
} from "../previewFund";
import DetailList from "./DetailList";
import styles from "../previewFund.module.scss";

interface ICurrencyFinancialsCard {
  detail: TSavedFundDetail;
  /** "1 of 2 currencies". */
  position: string;
}

/** Enough to cover every set of terms an admin can pick from. */
const TERMS_PAGE_SIZE = 100;

/**
 * One currency: how it is booked, what it takes and what it pays.
 *
 * It resolves its own core banking product and terms names from the ids the
 * fund carries, so the page passes nothing but the saved detail.
 */
export default function CurrencyFinancialsCard({
  detail,
  position,
}: ICurrencyFinancialsCard) {
  const currency = detail.currency_code;
  const currencyName = getCurrencyInfo(currency)?.name ?? currency;
  const symbol = getCurrencyInfo(currency)?.symbol ?? currency;

  const { data: products } = useMifosProduct({
    productType: detail.deposit_type ?? undefined,
    tenantId: detail.core_banking_tenant_id ?? undefined,
  });
  const { data: terms } = useGetTermsAndConditions({
    params: { page: 1, pageSize: TERMS_PAGE_SIZE },
  });

  const product = products?.find(
    (item) => item.id === Number(detail.core_banking_product_id),
  );
  const termsTitle = (terms?.data ?? []).find(
    (item: any) => item.id === detail.terms_and_condition_id,
  )?.title;

  const min = formatAmount(detail.minimum_investment, currency);
  const max = formatAmount(detail.maximum_investment, currency);
  const rate = detail.early_liquidation_charge_percentage;
  const isFree = isBlank(rate);
  const chart = detail.interest_rate_chart;
  const slabs = chart?.chart_slabs ?? [];

  return (
    <FormSection
      id={`financials-${currency}`}
      title={`Financials — ${currencyName}`}
      subtitle="How this currency is booked, what it takes and what it pays."
      icon={
        <span style={{ fontSize: 18, fontWeight: 700, color: "#713fff" }}>
          {symbol}
        </span>
      }
      meta={position}
    >
      <div className={styles.stackLoose}>
        <DetailList
          items={[
            { label: "Deposit type", value: humanise(detail.deposit_type) },
            {
              label: "Core banking",
              value: [detail.core_banking_tenant_id, product?.name]
                .filter(Boolean)
                .join(" · "),
            },
            {
              label: "Interest payment",
              value: detail.investment_interest_payment_type?.name,
            },
            {
              label: "Investment limits",
              value: min && max ? `${min} – ${max}` : undefined,
            },
            {
              label: "Investment size",
              value: detail.investment_size
                ? formatAmount(detail.investment_size, currency)
                : undefined,
            },
            { label: "Terms and conditions", value: termsTitle },
          ]}
        />

        <div
          className={`${styles.callout} ${isFree ? styles.calloutFree : ""}`}
        >
          <div>
            <div style={{ fontWeight: 500 }}>Early liquidation rate</div>
            <div style={{ marginTop: 2 }}>
              {isFree
                ? "No charge — customers can withdraw early for free."
                : "Charged on money taken out early."}
            </div>
          </div>
          <div className={styles.calloutValue}>
            {isFree ? "Free" : `${rate}%`}
          </div>
        </div>

        {slabs.length ? (
          <div>
            <div className={styles.chartHead}>
              <h3 className={styles.chartTitle}>Interest rate chart</h3>
              <span className={styles.chartMeta}>
                {[
                  chart?.name,
                  chart?.from_date
                    ? `effective ${formatDate(chart.from_date)}${
                        chart.to_date ? ` – ${formatDate(chart.to_date)}` : ""
                      }`
                    : undefined,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </div>
            <div className={styles.tableBox}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th scope="col" style={{ width: 48 }}>
                      #
                    </th>
                    <th scope="col">From</th>
                    <th scope="col">Up to</th>
                    <th scope="col">Rate</th>
                    <th scope="col">Label</th>
                  </tr>
                </thead>
                <tbody>
                  {slabs.map((slab, index) => (
                    <tr key={slab.id ?? index}>
                      <td>
                        <span className={styles.bandIndex}>{index + 1}</span>
                      </td>
                      <td>{formatAmount(slab.from_amount, currency) ?? "—"}</td>
                      <td>
                        {isBlank(slab.to_amount) ? (
                          <span className={styles.open}>and above</span>
                        ) : (
                          formatAmount(slab.to_amount, currency)
                        )}
                      </td>
                      <td className={styles.rate}>
                        {slab.interest_rate ?? 0}%
                      </td>
                      <td style={{ color: "#707070" }}>
                        {slab.description || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>
    </FormSection>
  );
}
